// GeneralErrorToastr.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import FadeOutToBottom from '../style/FadeOutToBottom.js';
import FadeInFromBottom from '../style/FadeInFromBottom.js';

import ErrorIconSource from '../../img/ic-error-grey.svg';
import CloseIconSource from '../../img/ic_close_white_16px.svg';

export class GeneralErrorToastr extends React.PureComponent {
  state = { isServer: true, show: true };
  nextTick = null;

  componentDidMount() {
    this.nextTick = setTimeout(() => this.setState({ isServer: false }), 0);
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  render() {
    const { isServer, show } = this.state;
    const { durationSec, errorObject, t, retry } = this.props;
    const isNetworkingIssue = errorObject?.name === 'ChunkLoadError';
    return isServer ? (
      <div className="error-placeholder" />
    ) : (
      <StyledGeneralErrorToastr $durationSec={durationSec} $show={show}>
        <ToastrBody>
          <ErrorIconWrapper>
            <ErrorIcon src={ErrorIconSource} />
          </ErrorIconWrapper>
          <ErrorMessage>
            {t(
              isNetworkingIssue
                ? 'retry_failed_reason'
                : 'something_went_wrong',
              {
                ns: TranslationNamespace.NOTIFICATION,
              }
            )}
          </ErrorMessage>
          <CloseButton>
            <CloseIcon
              src={CloseIconSource}
              alt={t('general_cancel', {
                ns: TranslationNamespace.GENERAL,
              })}
              onClick={() => {
                this.setState({ show: false });
              }}
            />
          </CloseButton>
        </ToastrBody>
        <ActionButton onClick={retry}>
          {t('retry', {
            ns: TranslationNamespace.DEFAULT,
          })}
        </ActionButton>
      </StyledGeneralErrorToastr>
    );
  }
}

GeneralErrorToastr.propTypes = {
  errorObject: PropTypes.object,
  durationSec: PropTypes.number,
  t: PropTypes.func.isRequired,
  retry: PropTypes.func,
};

GeneralErrorToastr.defaultProps = {
  errorObject: {},
  durationSec: 0.6,
  retry: () => {},
};

const StyledGeneralErrorToastr = styled.div`
  position: fixed;
  display: flex;
  width: 320px;
  padding: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 4px;
  background: var(--neutral-20-100, #474747);
  bottom: 20px;
  left: 20px;
  z-index: 199;
  ${media.tablet`
    width: 528px;
    left: 0;
    right: 0;
    margin: 0 auto;
  `};
  ${media.mobile`
    width: unset;
    left: 20px;
    right: 20px;
  `};

  animation: ${({ $durationSec }) => $durationSec}s forwards
    ${({ $show }) => ($show ? FadeInFromBottom : FadeOutToBottom)};
`;

const ToastrBody = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

const ErrorIconWrapper = styled.div`
  padding: 3px 3px 0;
`;

const ErrorIcon = styled.img`
  width: 18px;
  height: 18px;
`;

const ErrorMessage = styled.div`
  flex: 1;
  padding: 2px 4px 0;
  color: var(--font-color-primary-on-dark, #fff);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
  letter-spacing: 0.014px;
`;

const CloseButton = styled.button`
  border: none;
  padding-right: 0;
`;

const CloseIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const ActionButton = styled.button`
  border: none;
  display: flex;
  width: 100%;
  height: 28px;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: var(--button-color-primary-default, #00d2be);

  color: var(--font-color-primary-on-light, #191919);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: 0.014px;
`;

export default withTranslation()(GeneralErrorToastr);
